import { Dispatch, SetStateAction, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import { toast } from '@sales-center/components'

import { isSameField } from '@user/components/Settings/PendingLeadSettings/helpers'
import { LeadType } from '@user/components/Leads/LeadFilters/typings'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import {
  addAdditionalRequiredFields,
  IAutoReviewAdditionalField,
  IAutoReviewSettingsInput,
  removeAdditionalRequiredField,
} from '@user/store/autoReviewSettings'

type Props = {
  autoReviewEnabled: boolean
  selectedTypes: LeadType[]
  requiredAdditionalFields: IAutoReviewAdditionalField[]
  setRequiredAdditionalFields: Dispatch<
    SetStateAction<IAutoReviewAdditionalField[]>
  >
  resyncRequiredFields: () => Promise<void>
  save: (next: IAutoReviewSettingsInput) => Promise<void>
}

export const useHandlers = (props: Props) => {
  const {
    autoReviewEnabled,
    selectedTypes,
    requiredAdditionalFields,
    setRequiredAdditionalFields,
    resyncRequiredFields,
    save,
  } = props
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const handleToggleAutoReview = useCallback(() => {
    void save({
      enabled: !autoReviewEnabled,
      leadTypes: selectedTypes,
    })
  }, [autoReviewEnabled, selectedTypes, save])

  const handleManageAssignmentRules = useCallback(() => {
    navigate('/settings/assignmentRules')
  }, [navigate])

  const handleToggleType = useCallback(
    (value: string) => {
      const type = value as LeadType
      const next =
        selectedTypes.indexOf(type) === -1
          ? [...selectedTypes, type]
          : selectedTypes.filter((selectedType) => selectedType !== type)

      void save({
        enabled: autoReviewEnabled,
        leadTypes: next,
      })
    },
    [autoReviewEnabled, selectedTypes, save],
  )

  const handleManageFormSettings = useCallback(() => {
    navigate('/settings/formSettings')
  }, [navigate])

  const handleAddFields = useCallback(
    async (selection: IAutoReviewAdditionalField[]) => {
      const additions = selection.filter(
        (field, index) =>
          !requiredAdditionalFields.some((added) =>
            isSameField(added, field),
          ) &&
          selection.findIndex((other) => isSameField(other, field)) === index,
      )

      // The endpoint 400s on an empty body, so never send an empty add.
      if (additions.length === 0) return

      setRequiredAdditionalFields((current) => [...current, ...additions])

      const result = await dispatch(
        addAdditionalRequiredFields(
          additions.map(({ fieldId, isCustomField }) => ({
            fieldId,
            isCustomField,
          })),
        ),
      )

      if (!addAdditionalRequiredFields.fulfilled.match(result)) {
        toast.error('Unable to add the required fields.')
        // A mixed batch can half-apply on the server, so resync rather than
        // trusting a local rollback.
        await resyncRequiredFields()
      }
    },
    [
      requiredAdditionalFields,
      setRequiredAdditionalFields,
      dispatch,
      resyncRequiredFields,
    ],
  )

  const handleRemoveField = useCallback(
    async (field: IAutoReviewAdditionalField) => {
      const previous = requiredAdditionalFields

      setRequiredAdditionalFields((current) =>
        current.filter((entry) => !isSameField(entry, field)),
      )

      const result = await dispatch(
        removeAdditionalRequiredField({
          fieldId: field.fieldId,
          isCustomField: field.isCustomField,
        }),
      )

      if (!removeAdditionalRequiredField.fulfilled.match(result)) {
        setRequiredAdditionalFields(previous)
        toast.error('Unable to remove the required field.')
      }
    },
    [requiredAdditionalFields, setRequiredAdditionalFields, dispatch],
  )

  return {
    handleToggleAutoReview,
    handleToggleType,
    handleManageAssignmentRules,
    handleManageFormSettings,
    handleAddFields,
    handleRemoveField,
  }
}
